import React from 'react';
import styles from './styles.module.scss';
import { Button, Flex, Modal, PostIcon, Typography } from 'shared/_ui';
import { CONTROL_IDS, REF_IDS } from 'shared/consts';
import { daysUntilDate, useStoreUiControls } from 'shared/lib';
import { useTranslation } from 'react-i18next';
import { useRefContext } from 'shared/context';
import { useFetchUserInfo } from 'entities/Connection';

const PremiumExpirationWarningThreeDays: React.FC = () => {
  const { t } = useTranslation();

  const [isMounted, setIsMounted] = React.useState<boolean>(false);
  const shownStorageKeyRef = React.useRef<string | null>(null);

  const { isOpen, close } = useStoreUiControls(
    CONTROL_IDS.PREMIUM_EXPIRATION_BANNER
  );

  const { open: openTariffs } = useStoreUiControls(CONTROL_IDS.AVAILABLE_PLANS);
  const { refs } = useRefContext();

  const scrollToTariffList = () => {
    const layoutRef = refs.current[REF_IDS.LAYOUT];
    const availablePlansRef = refs.current[REF_IDS.AVAILABLE_PLANS];

    if (layoutRef && availablePlansRef) {
      const scrollOffset = 32;

      const elementPosition =
        availablePlansRef.getBoundingClientRect().top + layoutRef.scrollTop;
      const offsetPosition = elementPosition - scrollOffset;

      layoutRef.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const markAsShown = React.useCallback(() => {
    if (shownStorageKeyRef.current) {
      window.localStorage.setItem(shownStorageKeyRef.current, 'true');
    }
  }, []);

  const onClick = () => {
    close();
    markAsShown();
    openTariffs();
    scrollToTariffList();
  };

  const { data: userInfo } = useFetchUserInfo(null);
  React.useEffect(() => {
    if (!userInfo || !userInfo.paidTo) {
      setIsMounted(false);
      return;
    }

    const daysLeft = daysUntilDate(userInfo.paidTo);
    const warningDays = userInfo.pricingRegion === 'IN' ? 2 : 3;
    const shouldShow = daysLeft <= warningDays && daysLeft > 1;
    const storageKey = [
      'premium-expiration-warning',
      userInfo.pricingRegion ?? 'DEFAULT',
      userInfo.paidTo,
    ].join(':');
    const wasShown = window.localStorage.getItem(storageKey) === 'true';
    const shownInCurrentRender = shownStorageKeyRef.current === storageKey;
    const shouldMount =
      shouldShow && (isOpen === null || isOpen) && (!wasShown || shownInCurrentRender);

    if (shouldMount && !wasShown) {
      shownStorageKeyRef.current = storageKey;
      window.localStorage.setItem(storageKey, 'true');
    }

    setIsMounted(shouldMount);
  }, [isOpen, userInfo])

  if (!isMounted) return null;

  return (
    <Modal isOpen={isOpen} onClose={()=>{close();markAsShown()}}>
      <Flex
        className={styles.container}
        direction="column"
        spacing={32}
        align="center"
      >
        <PostIcon className={styles.post} />
        <Flex direction="column" spacing={24}>
          <Typography.Title level={1}>
            {t('banner.premiumExpiration.title')}
          </Typography.Title>
          <Flex direction="column" spacing={8}>
            <Typography.Text size={3}>
              {t('banner.premiumExpiration.subtitle', {
                count: userInfo?.pricingRegion === 'IN' ? 2 : 3,
              })}
            </Typography.Text>
            <Typography.Text type="secondary" size={3}>
              {t('banner.premiumExpiration.description')}
            </Typography.Text>
          </Flex>
        </Flex>
        <Button className={styles.button} onClick={onClick} type="primary">
          {t('banner.premiumExpiration.button')}
        </Button>
      </Flex>
    </Modal>
  );
};

export { PremiumExpirationWarningThreeDays };
